'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { submitGeneralFeedback } from '@/app/actions/productFeedback'
import { captureAnalyticsEvent } from '@/utils/analytics'
import { Button } from '@astryxdesign/core/Button'
import { TextInput } from '@astryxdesign/core/TextInput'
import { TextArea } from '@astryxdesign/core/TextArea'
import { Selector } from '@astryxdesign/core/Selector'
import ModalDialog from '@/app/components/ModalDialog'

const TOPIC_OPTIONS = [
  'Bug report',
  'Confusing copy or UX',
  'Feature request',
  'Something I expected to happen',
  'General feedback',
]

const FEEDBACK_MODAL_Z_INDEX = 1100
const NEAR_PAGE_BOTTOM_OFFSET = 240

function isNearPageBottom() {
  const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight
  return scrollableHeight > 0 && window.scrollY >= scrollableHeight - NEAR_PAGE_BOTTOM_OFFSET
}

export default function FeedbackButton() {
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)
  const [nearPageBottom, setNearPageBottom] = useState(false)
  const [topic, setTopic] = useState(TOPIC_OPTIONS[0])
  const [contactEmail, setContactEmail] = useState('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState(null)

  useEffect(() => {
    function openFeedbackModal(event) {
      const requestedTopic = event?.detail?.topic
      if (TOPIC_OPTIONS.includes(requestedTopic)) {
        setTopic(requestedTopic)
      }
      setIsOpen(true)
    }

    window.addEventListener('parnasa:open-feedback-modal', openFeedbackModal)
    return () => {
      window.removeEventListener('parnasa:open-feedback-modal', openFeedbackModal)
    }
  }, [])

  useEffect(() => {
    // ponytail: pagination lives at the page bottom; hide while near it so the
    // floating button never covers Prev/Next. Position-based, no resize observer.
    function updateNearPageBottom() {
      setNearPageBottom(isNearPageBottom())
    }
    updateNearPageBottom()
    window.addEventListener('scroll', updateNearPageBottom, { passive: true })
    window.addEventListener('resize', updateNearPageBottom)
    return () => {
      window.removeEventListener('scroll', updateNearPageBottom)
      window.removeEventListener('resize', updateNearPageBottom)
    }
  }, [])

  async function handleSubmit(event) {
    event.preventDefault()
    setIsSubmitting(true)
    setStatus(null)

    let result
    try {
      result = await submitGeneralFeedback({
        contactEmail,
        pagePath: pathname || '',
        topic,
        message,
        source: 'floating_feedback_button',
      })
    } catch (error) {
      const errorMessage = error?.message || 'We could not send that feedback. Please try again.'
      setStatus({ type: 'error', text: errorMessage })
      setIsSubmitting(false)
      return
    }

    if (result?.error) {
      setStatus({ type: 'error', text: result.error })
      setIsSubmitting(false)
      return
    }

    captureAnalyticsEvent('general_feedback_submitted', {
      event_category: 'feedback',
      topic,
      page_path: pathname || '',
      has_contact_email: Boolean(contactEmail.trim()),
      email_notification_warning: Boolean(result?.warning),
    })

    setStatus({
      type: 'success',
      text: result?.warning || 'Thanks. Your feedback was sent.',
    })
    setMessage('')
    setContactEmail('')
    setIsSubmitting(false)
  }

  function closeModal() {
    setIsOpen(false)
    setStatus(null)
  }

  return (
    <>
      {!nearPageBottom ? (
        <Button
          type="button"
          variant="primary"
          label="Send feedback"
          className="site-feedback-floating-button"
          onClick={() => setIsOpen(true)}
        />
      ) : null}

      {isOpen ? (
        <ModalDialog
          className="modal-shell"
          data-feedback-modal="true"
          labelledById="feedback-dialog-title"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: FEEDBACK_MODAL_Z_INDEX }}
          onClose={closeModal}
        >
          <div className="glass-panel animate-fade-in modal-card" style={{ padding: '1.5rem', maxWidth: '36rem' }}>
            <div className="section-header" style={{ marginBottom: '1rem' }}>
              <div>
                <h2 id="feedback-dialog-title" style={{ margin: 0, fontSize: '1.35rem' }}>Send feedback</h2>
                <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  Send anything unclear, broken, surprising, or worth improving.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem' }}>
              <Selector
                label="What kind of feedback is this?"
                options={TOPIC_OPTIONS}
                value={topic}
                onChange={setTopic}
              />

              <TextArea
                label="What should we know?"
                value={message}
                onChange={setMessage}
                rows={5}
                placeholder="Tell us what happened, what you expected, or what would make this better."
                isRequired
              />

              <TextInput
                type="email"
                label="Email for follow-up"
                isOptional
                autoComplete="email"
                value={contactEmail}
                onChange={setContactEmail}
                placeholder="you@example.com"
              />

              {status ? (
                <div style={{ padding: '0.85rem 1rem', borderRadius: '0.75rem', background: status.type === 'error' ? 'var(--status-danger-bg)' : 'var(--status-success-bg)', color: status.type === 'error' ? 'var(--status-danger-fg)' : 'var(--status-success-fg)', fontSize: '0.92rem' }}>
                  {status.text}
                </div>
              ) : null}

              <div className="panel-actions" style={{ justifyContent: 'space-between' }}>
                <Button type="button" variant="secondary" label="Close" onClick={closeModal} width="auto" />
                <Button
                  type="submit"
                  variant="primary"
                  label={isSubmitting ? 'Sending...' : 'Send feedback'}
                  isDisabled={isSubmitting}
                  width="auto"
                />
              </div>
            </form>
          </div>
        </ModalDialog>
      ) : null}
    </>
  )
}
